import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingCart, Menu, X } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { MENU_BRACKETS, MENU_PRODUCT_GROUPS } from "../data/range";

// Dark sticky nav, 88px tall. Brackets + Products open hover mega-menus on
// desktop. Below 1024px the centre links and Shop button hide, replaced by a
// hamburger that opens a right-hand slide-in drawer listing every destination.
const MENUS = [
  { label: "Brackets", to: "/products/brackets", items: MENU_BRACKETS, width: 360, cols: 1 },
  { label: "Products", to: "/products", groups: MENU_PRODUCT_GROUPS, width: 880, cols: MENU_PRODUCT_GROUPS.length },
];

// Build Ideas deliberately isn't a top-level link — it's reachable from the
// Products menu, the homepage and the footer, keeping the main nav shop-first.
const LINKS = [
  { to: "/reviews", label: "Reviews" },
  { to: "/trade", label: "Trade" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

// Mobile drawer, grouped the same way as the desktop Products menu so the
// whole range (extensions and accessories included) is one short scroll.
// "All brackets" opens the group, which is why the drawer no longer carries a
// separate fixed "Shop brackets" button.
const DRAWER_GROUPS = [
  {
    heading: "Brackets",
    links: [
      { to: "/products/brackets", label: "All brackets", all: true },
      { to: "/products/supported-bracket", label: "Supported bracket" },
      { to: "/products/flat-bracket", label: "Flat bracket" },
      { to: "/products/bulk-packs", label: "Bulk packs" },
      { to: "/products/anchor-bracket", label: "Anchor base bracket", note: "Soon" },
    ],
  },
  {
    heading: "Extensions",
    links: [
      { to: "/products/fence-height-extensions", label: "Fence extensions & joiners" },
      { to: "/products/animal-extensions", label: "Angled animal extensions" },
    ],
  },
  {
    heading: "Accessories & posts",
    links: [
      { to: "/products/installation-kits", label: "Installation kits" },
      { to: "/products/pre-drilled-galvanised-angle", label: "Pre-drilled steel angle" },
      { to: "/posts", label: "Fence posts" },
      { to: "/products", label: "All products", all: true },
    ],
  },
];

const DRAWER_INFO = [
  { to: "/applications", label: "Build ideas" },
  { to: "/blog", label: "Guides" },
  { to: "/reviews", label: "Reviews" },
  { to: "/faq", label: "FAQ" },
  { to: "/returns-warranty", label: "Shipping & returns" },
  { to: "/trade", label: "Trade" },
  { to: "/international-shipping", label: "International" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const slug = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function Nav() {
  const [openMenu, setOpenMenu] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const { itemCount, openCart } = useCart();
  const closeTimer = useRef(null);
  const drawerRef = useRef(null);
  const toggleRef = useRef(null);
  const closeRef = useRef(null);

  // Every navigation closes the menus. location.key changes even when the
  // link points at the page already open, so tapping it still shuts the drawer.
  useEffect(() => {
    setOpenMenu(null);
    setDrawerOpen(false);
  }, [location.key]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Below 1024px the desktop panels are display:none, so force them shut; above
  // it the hamburger is hidden, so close the drawer (otherwise a rotated tablet
  // could be left with a locked page behind an invisible drawer).
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1024px)");
    const sync = () => {
      if (mq.matches) setOpenMenu(null);
      else setDrawerOpen(false);
    };
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  // Lock body scroll while the drawer is open.
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  // Keyboard: focus moves into the drawer when it opens, Tab stays inside it,
  // Escape closes it, and focus goes back to the menu button afterwards.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    // Captured now, not in the cleanup: by then the elements may be gone.
    const drawer = drawerRef.current;
    const toggle = toggleRef.current;
    closeRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setDrawerOpen(false);
        return;
      }
      if (e.key !== "Tab" || !drawerRef.current) return;
      const focusable = drawerRef.current.querySelectorAll("a[href], button:not([disabled])");
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (drawer?.contains(document.activeElement)) toggle?.focus();
    };
  }, [drawerOpen]);

  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  const open = (label) => { window.clearTimeout(closeTimer.current); setOpenMenu(label); };
  const scheduleClose = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpenMenu(null), 120);
  };
  const closeDrawer = () => setDrawerOpen(false);

  return (
    <nav className={`pd-nav${scrolled ? " pd-nav--scrolled" : ""}`} data-testid="main-nav" aria-label="Main">
      <div className="pd-nav-inner">
        <Link to="/" className="pd-nav-logo" data-testid="nav-logo-link" aria-label="StakeTek home">
          <img src="/images/logo-nav-orange.png" alt="StakeTek" width="226" height="40" />
        </Link>

        <div className="pd-nav-links">
          {MENUS.map((menu) => (
            <div
              key={menu.label}
              className="pd-nav-menu"
              onMouseEnter={() => open(menu.label)}
              onMouseLeave={scheduleClose}
              onFocus={() => open(menu.label)}
              onBlur={scheduleClose}
            >
              <NavLink
                to={menu.to}
                end
                className={({ isActive }) => `pd-nav-link${isActive ? " is-active" : ""}`}
                data-testid={`nav-link-${slug(menu.label)}`}
                aria-expanded={openMenu === menu.label}
              >
                {menu.label}
                <span aria-hidden="true" className="pd-nav-caret">▼</span>
              </NavLink>
              <div
                className={`pd-nav-panel${menu.groups ? " pd-nav-panel--groups" : ""}${openMenu === menu.label ? " is-open" : ""}`}
                style={{ width: menu.width, gridTemplateColumns: `repeat(${menu.cols}, 1fr)` }}
                data-testid={`nav-${slug(menu.label)}-menu`}
              >
                {menu.groups
                  ? menu.groups.map((group) => (
                      <div key={group.heading} className="pd-nav-group">
                        <span className="pd-nav-group-head">{group.heading}</span>
                        {group.items.map((item) => (
                          <Link
                            key={item.to + item.name}
                            to={item.to}
                            className="pd-nav-grouplink"
                            data-testid={`nav-dropdown-${slug(item.name)}`}
                          >
                            <span className="pd-nav-row-name">{item.name}</span>
                            <span className="pd-nav-row-note">{item.note}</span>
                          </Link>
                        ))}
                      </div>
                    ))
                  : menu.items.map((item) => (
                      <Link
                        key={item.to + item.name}
                        to={item.to}
                        className="pd-nav-row"
                        data-testid={`nav-dropdown-${slug(item.name)}`}
                      >
                        {item.img ? (
                          // lazy: the panel is hidden until hover, so these 48px thumbs
                          // shouldn't cost every page load ~0.5MB up front.
                          <img src={item.img} alt="" aria-hidden="true" style={{ objectFit: item.fit }} loading="lazy" decoding="async" />
                        ) : (
                          // Photo pending — neutral slot, same footprint as the thumbnails.
                          <span aria-hidden="true" style={{ display: "grid", placeItems: "center", width: 48, height: 48, flex: "none", background: "var(--pd-parchment, #EFECE5)", border: "1px solid var(--pd-border, #E2DED6)", color: "var(--pd-burnt, #B45309)", fontSize: 9.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase" }}>New</span>
                        )}
                        <span>
                          <span className="pd-nav-row-name">{item.name}</span>
                          <span className="pd-nav-row-note">{item.note}</span>
                        </span>
                      </Link>
                    ))}
              </div>
            </div>
          ))}

          {LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) => `pd-nav-link${isActive ? " is-active" : ""}`}
              data-testid={`nav-link-${slug(link.label)}`}
            >
              {link.label}
            </NavLink>
          ))}
        </div>

        <div className="pd-nav-actions">
          <Link to="/products/brackets" className="pd-nav-cta" data-testid="nav-cta-shop">
            Shop brackets
          </Link>
          <button
            type="button"
            className="pd-nav-icon-btn"
            onClick={openCart}
            aria-label={`Cart${itemCount > 0 ? ` (${itemCount} item${itemCount !== 1 ? "s" : ""})` : ", empty"}`}
            data-testid="nav-cart-btn"
          >
            <ShoppingCart size={19} strokeWidth={1.9} />
            {itemCount > 0 && (
              <span className="pd-nav-badge" aria-hidden="true">{itemCount > 99 ? "99+" : itemCount}</span>
            )}
          </button>
          <button
            ref={toggleRef}
            type="button"
            className="pd-nav-toggle"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            aria-expanded={drawerOpen}
            aria-controls="site-menu"
            data-testid="nav-hamburger-btn"
          >
            <Menu size={22} strokeWidth={2} />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`pd-nav-scrim${drawerOpen ? " is-open" : ""}`}
        onClick={closeDrawer}
        aria-hidden="true"
      />
      <div
        ref={drawerRef}
        id="site-menu"
        className={`pd-nav-drawer${drawerOpen ? " is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Site menu"
        inert={!drawerOpen}
        data-testid="mobile-menu"
      >
        <div className="pd-nav-drawer-head">
          <span>Menu</span>
          <button
            ref={closeRef}
            type="button"
            className="pd-nav-drawer-close"
            onClick={closeDrawer}
            aria-label="Close menu"
            data-testid="mobile-menu-close"
          >
            <X size={20} strokeWidth={2} />
          </button>
        </div>
        <div className="pd-nav-drawer-list">
          {DRAWER_GROUPS.map((group) => (
            <section key={group.heading} className="pd-nav-dgroup" aria-labelledby={`dgroup-${slug(group.heading)}`}>
              <h2 id={`dgroup-${slug(group.heading)}`} className="pd-nav-dgroup-head">{group.heading}</h2>
              {group.links.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end
                  onClick={closeDrawer}
                  className={({ isActive }) => `pd-nav-dlink${link.all ? " pd-nav-dlink--all" : ""}${isActive ? " is-active" : ""}`}
                  data-testid={`mobile-link-${slug(link.label)}`}
                >
                  {link.label}
                  {link.note && <span className="pd-nav-dlink-note">{link.note}</span>}
                </NavLink>
              ))}
            </section>
          ))}
          <section className="pd-nav-dgroup" aria-labelledby="dgroup-help">
            <h2 id="dgroup-help" className="pd-nav-dgroup-head">Help &amp; company</h2>
            <div className="pd-nav-dgrid">
              {DRAWER_INFO.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end
                  onClick={closeDrawer}
                  className={({ isActive }) => (isActive ? "is-active" : undefined)}
                  data-testid={`mobile-link-${slug(link.label)}`}
                >
                  {link.label}
                </NavLink>
              ))}
            </div>
          </section>
        </div>
      </div>
    </nav>
  );
}
